const GOLDEN_ANGLE_DEGREES = 137.508;
const HUE_OFFSET_DEGREES = 18;
const CURATED_SPEAKER_PALETTE_SIZE = 7;

// The first seven speakers use a restrained Nord-style accent palette so the
// common cases feel deliberate instead of algorithmic. After that we fall back
// to generated colors for larger panels.
const CURATED_LIGHT_SPEAKER_COLORS = [
  '#81A1C1',
  '#88C0D0',
  '#8FBCBB',
  '#A3BE8C',
  '#EBCB8B',
  '#D08770',
  '#B48EAD',
];

const CURATED_DARK_SPEAKER_COLORS = [
  '#5E81AC',
  '#5E97A5',
  '#5E9694',
  '#7E9969',
  '#B08D54',
  '#A96E5C',
  '#8F739D',
];

const LIGHT_MODE_SATURATION_STEPS = [48, 54, 46, 58];
const LIGHT_MODE_LIGHTNESS_STEPS = [58, 64, 56, 62, 68];
const DARK_MODE_SATURATION_STEPS = [58, 54, 50, 62];
const DARK_MODE_LIGHTNESS_STEPS = [54, 60, 50, 56, 62];

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const inferDarkMode = (isDarkMode?: boolean): boolean => {
  if (typeof isDarkMode === 'boolean') {
    return isDarkMode;
  }

  if (typeof document !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }

  return false;
};

const normalizeSpeakerId = (speakerId: number): number => (
  Number.isFinite(Number(speakerId)) ? Number(speakerId) : 0
);

const hslToHex = (hue: number, saturation: number, lightness: number): string => {
  const normalizedHue = ((hue % 360) + 360) % 360;
  const safeSaturation = clamp(saturation, 0, 100) / 100;
  const safeLightness = clamp(lightness, 0, 100) / 100;

  const chroma = (1 - Math.abs((2 * safeLightness) - 1)) * safeSaturation;
  const huePrime = normalizedHue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));

  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = x;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = x;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = x;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = x;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const matchLightness = safeLightness - (chroma / 2);
  const toHexChannel = (channel: number): string => (
    Math.round((channel + matchLightness) * 255)
      .toString(16)
      .padStart(2, '0')
  );

  return `#${toHexChannel(red)}${toHexChannel(green)}${toHexChannel(blue)}`;
};

const getSpeakerHue = (speakerId: number): number => (
  (normalizeSpeakerId(speakerId) * GOLDEN_ANGLE_DEGREES + HUE_OFFSET_DEGREES) % 360
);

const getSpeakerThemeColor = (speakerId: number, isDarkMode?: boolean): string => {
  const safeSpeakerId = normalizeSpeakerId(speakerId);
  const darkMode = inferDarkMode(isDarkMode);
  const hue = getSpeakerHue(safeSpeakerId);
  const saturationSteps = darkMode ? DARK_MODE_SATURATION_STEPS : LIGHT_MODE_SATURATION_STEPS;
  const lightnessSteps = darkMode ? DARK_MODE_LIGHTNESS_STEPS : LIGHT_MODE_LIGHTNESS_STEPS;
  let saturation = saturationSteps[safeSpeakerId % saturationSteps.length];
  let lightness = lightnessSteps[Math.floor(safeSpeakerId / saturationSteps.length) % lightnessSteps.length];

  if (hue >= 42 && hue <= 84) {
    saturation += darkMode ? 4 : 6;
    lightness += darkMode ? -2 : 4;
  }

  return hslToHex(hue, saturation, lightness);
};

const rgbaFromHex = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getSpeakerColorValue = (speakerId: number, isDarkMode?: boolean): string => (
  (() => {
    const safeSpeakerId = normalizeSpeakerId(speakerId);
    const palette = inferDarkMode(isDarkMode) ? CURATED_DARK_SPEAKER_COLORS : CURATED_LIGHT_SPEAKER_COLORS;

    if (safeSpeakerId >= 0 && safeSpeakerId < CURATED_SPEAKER_PALETTE_SIZE) {
      return palette[safeSpeakerId];
    }

    return getSpeakerThemeColor(safeSpeakerId, isDarkMode);
  })()
);

export const getSpeakerGradientValue = (speakerId: number, isDarkMode?: boolean): string => {
  const color = getSpeakerColorValue(speakerId, isDarkMode);
  return `linear-gradient(to right, ${rgbaFromHex(color, 0.7)}, ${rgbaFromHex(color, 1)}, ${rgbaFromHex(color, 0.7)})`;
};
